<template>
  <div
    id="app"
    :class="{'clear-layout': isClearHeader}"
  >
    <div>
      <div class="flex">
        <div
          v-if="!publicState"
          class="side-bar__wrap relative"
        >
          <div
            class="side-bar__header--shrunk-control"
            :class="{ 'mirrored': isShrunk }"
          >
            <ShrunkControl
              v-if="!isClearHeader"
              :key="getVisibilityState"
              v-tooltip="{ content: !isShrunk ? 'Collapse Sidebar' : 'Expand Sidebar', class: 'tooltip--sidebar' }"
              @click="setShrunkPreference(!isShrunk)"
            />
          </div>
          <Sidebar :class="{ 'hide-header': isClearHeader }" />
        </div>
        <div class="app-root">
          <gl-header :class="{'hide-header': isClearHeader}" />
          <transition
            mode="out-in"
            name="fade"
          >
            <router-view />
          </transition>
          <!-- <UserGuideTooltip /> -->
        </div>
      </div>
      <gl-disclaimer v-if="isDisclaimerShown" />
    </div>
    <tooltip />
    <div
      ref="fakeTooltip"
      v-popover:tooltip=""
    />
    <RequestsAttentionModal
      v-if="requestsAttentionModel"
      v-model="requestsAttentionModel"
    />
    <GlMissionControl v-model="isMissionControlOpen" />
  </div>
</template>

<script>
// Vuex
import { mapActions, mapGetters, mapMutations, mapState } from 'vuex'
// Components
import GlHeader from '@/components/layout/gl-header'
import Sidebar from '@/components/sidePanelMenu'
// import UserGuideTooltip from '@/components/gl-user-guide-tooltip'
// Utils
import { setDirection } from '@/utils/feature-control'
import { ScoringFormulaList } from '@/utils/globalConstants'
import { dispatchEventToRef } from '@/utils/helpers'
import config from "@/utils/appConfig";
import appConfig from '@/utils/appConfig'
import { getAdvancedManifest, getEssentialManifest } from '@/utils/manifest'
import { featureAccess, getPublicState } from "@/utils/accesses";
import { BRAND_FAVICON_URL } from '@/utils/brand'
// Services
import defineAbilityFor from "@/services/defineAbilityFor";
// Static
import themes from "@/assets/styles/themes.json";
import GlDisclaimer from '@/components/gl-disclaimer.vue'
import RequestsAttentionModal from '@/components/popups/RequestsAttentionModal.vue'
import GlMissionControl from '@/components/gl-mission-control.vue'
import { endSession, SESSION_END_KEY } from '@/utils/session'

// Two Shift taps inside this window open the mission-control grid.
const DOUBLE_SHIFT_DELAY = 350

const ShrunkControl = () => {
  return import(`@/assets/svg/header/ShrunkControl.svg?vue-component`)
};

export default {
  components: {
    GlMissionControl,
    RequestsAttentionModal,
    GlDisclaimer,
    GlHeader,
    Sidebar,
    ShrunkControl,
    // UserGuideTooltip,
  },
  data() {
    return {
      clearHeaderRouteNames: ['register', 'login', 'integration-connect', 'enter-code'],
      themesData: themes,
      resultThemeData: {},
      currentFiatKey: '',
      disclaimerRouteNames: ['analytics', 'alerts', 'entity-list', 'entity-management-list'],
      isMissionControlOpen: false,
      lastShiftAt: 0,
    }
  },
  computed: {
    ...mapState('analytics', ['coinType', 'coinData', 'activeCoinData', 'currencyList',]),
    ...mapState('user', ['fiatList', 'requestsAttentionModal']),
    ...mapState('sidebar', ['isShrunk']),
    ...mapGetters('app', ['getVisibilityState']),
    requestsAttentionModel: {
      get() {
        return this.requestsAttentionModal
      },
      set(val) {
        this.SET_REQUESTS_ATTENTION_MODAL(val)
      }
    },
    isDisclaimerShown() {
      return this.disclaimerRouteNames.includes(this.$route.name)
    },
    publicState() {
      return this.getPublicState(this.$route.query)
    },
    isClearHeader() {
      return this.clearHeaderRouteNames.includes(this.$route.name)
    },
  },
  watch: {
    coinType: {
      handler(role) {
        this.updateAbilities(role)
      },
      immediate: true,
    },
    activeCoinData: {
      handler(role) {
        this.updateAbilities(role)
      },
      immediate: true,
    },
    $route: {
      handler() {
        this.$nextTick(() => {
          const disclaimerHeight = document.querySelector('.gl-analytics__disclaimer')
          const doc = document.documentElement
          doc.style.setProperty('--disclaimer-height', `${this.isDisclaimerShown ? disclaimerHeight.clientHeight : 0}px`)
          this.hideTooltip()
        })

      },
      immediate: true,
    },
  },
  async created() {
    this.resultThemeData = this.mergeObjects(this.themesData['default'], this.themesData[config.VUE_APP_THEME])
    const faviconLink = document.getElementById("favicon");
    if (faviconLink && BRAND_FAVICON_URL) {
      faviconLink.href = BRAND_FAVICON_URL;
    } else if (faviconLink && config.VUE_APP_THEME) {
      faviconLink.href = `/assets/themes/${config.VUE_APP_THEME}/favicon.ico`;
    }
    this.setGlobalStyles()

    const advancedManifest = getAdvancedManifest()
    this.SET_COIN_CURRENCY_LIST(advancedManifest.list)

    const documentHeight = () => {
        const disclaimerHeight = document.querySelector('.gl-analytics__disclaimer')
        const doc = document.documentElement

        doc.style.setProperty('--disclaimer-height', `${this.isDisclaimerShown ? disclaimerHeight.clientHeight : 0}px`)
        doc.style.setProperty('--doc-height', `${window.innerHeight}px`)
    }
    window.addEventListener('resize', documentHeight)
    documentHeight()
    const coin = this.currencyList[0]
    this.SET_COIN_DATA(coin)
    this.SET_COIN_TYPE(coin.key)

    let essentialManifest = { list: [], map: {} }
    if (this.featureAccess('ESSENTIAL')) {
      essentialManifest = getEssentialManifest()
      this.SET_COIN_ESSENTIAL_CURRENCY_LIST(essentialManifest.list)
    }

    this.SET_CURRENCY_LIST_MAP({
      ...advancedManifest.map,
      ...essentialManifest.map,
    })

    const initScoringFormula = this.featureAccess('FORMULA_SELECT')
      ? localStorage.getItem('scoringFormula')
        || (ScoringFormulaList.find(f => f.key === appConfig.VUE_APP_SCORING_FORMULA) && ScoringFormulaList.find(f => f.key === appConfig.VUE_APP_SCORING_FORMULA).key)
        || ScoringFormulaList[0].key
      : appConfig.VUE_APP_SCORING_FORMULA

    localStorage.setItem('scoringFormula', initScoringFormula)

    await this.updateAbilities(this.coinType)
    this.currentFiatKey = localStorage.getItem('currentFiatKey') || this.fiatList[0].key
    window.addEventListener('storage', this.checkLocalStorage);
    setDirection(Number(appConfig.VUE_APP_SCORE_REQUEST_DIRECTION))
    this.$root.$on('mouseleave-for-fake-tooltip', () => {
      this.hideTooltip()
    })
  },
  mounted() {
    document.addEventListener('visibilitychange', this.handleVisibilityChange);
    window.addEventListener('keydown', this.handleDoubleShift, true);
  },
  beforeDestroy() {
    document.removeEventListener('visibilitychange', this.handleVisibilityChange);
    window.removeEventListener('keydown', this.handleDoubleShift, true);
  },
  methods: {
    ...mapMutations({
      SET_COIN_TYPE: 'analytics/SET_COIN_TYPE',
      SET_COIN_DATA: 'analytics/SET_COIN_DATA',
      SET_COIN_CURRENCY_LIST: 'analytics/SET_COIN_CURRENCY_LIST',
      SET_COIN_ESSENTIAL_CURRENCY_LIST: 'analytics/SET_COIN_ESSENTIAL_CURRENCY_LIST',
      SET_CURRENCY_LIST_MAP: 'analytics/SET_CURRENCY_LIST_MAP'
    }),
    ...mapMutations({

      SET_VISIBILITY_STATE: 'app/SET_VISIBILITY_STATE',
    }),
    ...mapMutations({
      SET_REQUESTS_ATTENTION_MODAL: 'user/SET_REQUESTS_ATTENTION_MODAL',
    }),
    ...mapActions('sidebar', ['setShrunkPreference']),
    getPublicState,
    dispatchEventToRef,
    featureAccess,
    // Shift, Shift — but only when nothing else was typed in between, so that
    // holding Shift for capitals never opens the grid.
    handleDoubleShift(event) {
      if (event.key !== 'Shift') {
        this.lastShiftAt = 0
        return
      }

      if (event.repeat || event.ctrlKey || event.metaKey || event.altKey) {
        this.lastShiftAt = 0
        return
      }

      const now = Date.now()

      if (now - this.lastShiftAt < DOUBLE_SHIFT_DELAY) {
        this.lastShiftAt = 0
        if (this.isClearHeader || this.publicState) return
        this.isMissionControlOpen = !this.isMissionControlOpen
      } else {
        this.lastShiftAt = now
      }
    },
    handleVisibilityChange() {
      this.SET_VISIBILITY_STATE(document.visibilityState)
    },
    checkLocalStorage(event) {
      // A session ended in another tab. Ending ours without re-broadcasting — two
      // tabs that both answer would ping-pong forever.
      if (event.key === SESSION_END_KEY && event.newValue) {
        const [reason] = event.newValue.split(':')
        endSession(reason, { broadcast: false })
        return
      }

      if (event.key === 'currentFiatKey') {
        const newData = event.newValue || null;
        if (newData !== this.currentFiatKey) {
          this.currentFiatKey = newData;
          location.reload()
        }
      }
    },
    renameObjectKeys(obj, prefix) {
      const result = {};

      for (const key in obj) {
        // eslint-disable-next-line no-prototype-builtins
        if (obj.hasOwnProperty(key)) {
          result[prefix + key] = obj[key];
        }
      }

      return result;
    },
    setGlobalStyles() {
      const { colors } = this.resultThemeData;
      const formattedColors = this.renameObjectKeys(colors, '--')
      const styleTag = document.createElement('style');

      styleTag.textContent = generateCSSFromJSON(formattedColors);
      document.head.appendChild(styleTag);

      function generateCSSFromJSON(jsonData) {
        let css = ':root {\n';

        for (const key in jsonData) {
          // eslint-disable-next-line no-prototype-builtins
          if (jsonData.hasOwnProperty(key)) {
            css += `${key}: ${jsonData[key]};\n`;
          }
        }

        css += '}';
        return css;
      }
    },
    mergeObjects(baseObj, newObj) {
      const result = baseObj;

      for (const key in newObj) {
        // eslint-disable-next-line no-prototype-builtins
        if (newObj.hasOwnProperty(key)) {
          if (typeof newObj[key] === 'object' && !Array.isArray(newObj[key])) {
            result[key] = this.mergeObjects(baseObj[key] || {}, newObj[key]);
          } else {
            // eslint-disable-next-line no-prototype-builtins
            result[key] = baseObj.hasOwnProperty(key) && baseObj[key] === newObj[key] ? baseObj[key] : newObj[key];
          }
        }
      }

      return result;
    },
    updateAbilities() {
      this.$ability.update(defineAbilityFor(this.activeCoinData?.key ?? this.coinData.key).rules);
    },
    hideTooltip() {
      const refEl = this.$refs.fakeTooltip
      if (refEl) this.dispatchEventToRef({ refEl, eventName: 'mouseleave'})
    },
  },
}
</script>

<style>
.side-bar__header--shrunk-control {
  display: flex;
  opacity: 0;
  width: 40px;
  height: 40px;
  align-items: center;
  justify-content: center;
  transition: opacity 0.3s ease;
  position: absolute;
  right: -20px;
  top: 16px;
  /* Above the page and its dropdowns, but under the modal layer (50): none of
     its ancestors makes a stacking context, so a higher value floats this
     chevron over the modal backdrop, unblurred and clickable. */
  z-index: 20;
  cursor: pointer;
  pointer-events: none;
  transition: opacity 0.3s ease;
}

.side-bar__header--shrunk-control svg:focus {
  outline: none;
}

.side-bar__wrap:hover .side-bar__header--shrunk-control {
  opacity: 1;
  pointer-events: auto;
}

.side-bar__header--shrunk-control svg {
  display: block;
  transition: transform 0.3s ease;
}

.side-bar__header--shrunk-control.mirrored svg {
  transform: scaleX(-1);
}

@media (max-width: 1023px) {
  .side-bar__header--shrunk-control {
    opacity: 1;
    pointer-events: auto;
  }
}
</style>
